import { useEffect, useState } from "react";
import { fetchTravelArticles } from "@/lib/strapi";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 12,
  pageCount: 1,
  total: 0,
};

const mergeTravelArticles = (currentArticles, incomingArticles) => {
  const seenArticleIds = new Set(currentArticles.map((article) => article.slug || article.id));
  const uniqueIncomingArticles = incomingArticles.filter((article) => {
    const key = article.slug || article.id;
    if (!key || seenArticleIds.has(key)) {
      return false;
    }

    seenArticleIds.add(key);
    return true;
  });

  return [...currentArticles, ...uniqueIncomingArticles];
};

export function useTravelArticles(limit = 60) {
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: limit });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadArticles = async () => {
      try {
        setIsLoading(true);
        setError("");
        const result = await fetchTravelArticles({ page: 1, pageSize: limit });
        if (mounted) {
          setArticles(result?.items || []);
          setPagination(result?.pagination || { ...DEFAULT_PAGINATION, pageSize: limit });
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load travel articles");
          setArticles([]);
          setPagination({ ...DEFAULT_PAGINATION, pageSize: limit });
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadArticles();

    return () => {
      mounted = false;
    };
  }, [limit]);

  const loadMore = async () => {
    if (isLoading || isLoadingMore || pagination.page >= pagination.pageCount) {
      return;
    }

    try {
      setIsLoadingMore(true);
      setError("");
      const result = await fetchTravelArticles({ page: pagination.page + 1, pageSize: limit });
      setArticles((currentArticles) => mergeTravelArticles(currentArticles, result?.items || []));
      setPagination(result?.pagination || pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load travel articles");
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    articles,
    pagination,
    isLoading,
    isLoadingMore,
    error,
    hasMore: pagination.page < pagination.pageCount,
    loadMore,
  };
}
